import {
  type Item,
  type ItemView,
  type ToolInput,
  type ToolName,
  type ToolOutput,
  toolSpecs,
} from '@hugo/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useSyncExternalStore } from 'react'
import { toast } from '@/components/ui/toast'
import { ApiError } from './api'
import { log } from './log'
import { refresher } from './refresh'
import { useSession } from './session'
import { callTool } from './tools'

/** The two outbox lists: drafts waiting for approval, and recently decided ones. */
export type OutboxList = 'pending' | 'recent'

export const queryKeys = {
  items: ['items'] as const,
  view: (view: ItemView) => ['items', 'view', view] as const,
  counts: ['items', 'counts'] as const,
  item: (id: string) => ['items', 'item', id] as const,
  summary: ['insights', 'summary'] as const,
  settings: ['settings'] as const,
  outbox: ['outbox'] as const,
  outboxList: (list: OutboxList) => ['outbox', list] as const,
  connectors: ['connectors'] as const,
  ai: ['ai'] as const,
  timeOff: ['time-off'] as const,
  accessTokens: ['access-tokens'] as const,
  headsUps: ['headsups'] as const,
  /** Under `items`: every change that refreshes the lists refreshes Today too. */
  today: ['items', 'today'] as const,
  /** Under `items` as well: the board follows every change to items. */
  board: ['items', 'board'] as const,
  /** Under `items`: tag counts follow item changes (and settings, which refresh items too). */
  tags: ['items', 'tags'] as const,
}

/** Data queries only run with a session (no 401s while signing out). */
export function useSignedIn(): boolean {
  return useSession().me !== null
}

export function useItems(view: ItemView) {
  return useQuery({
    queryKey: queryKeys.view(view),
    queryFn: async () => (await callTool('items.list', { view })).result.items,
    enabled: useSignedIn(),
  })
}

export function useCounts() {
  return useQuery({
    queryKey: queryKeys.counts,
    queryFn: async () => (await callTool('items.counts', {})).result,
    enabled: useSignedIn(),
  })
}

export function useItemDetail(id: string | null) {
  const signedIn = useSignedIn()
  return useQuery({
    queryKey: queryKeys.item(id ?? 'none'),
    queryFn: async () => (await callTool('item.get', { id: id ?? '' })).result,
    enabled: signedIn && id !== null,
  })
}

export function useSummary() {
  return useQuery({
    queryKey: queryKeys.summary,
    queryFn: async () => (await callTool('insights.summary', { period: 'this_week' })).result,
    enabled: useSignedIn(),
  })
}

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: async () => (await callTool('settings.get', {})).result,
    staleTime: 5 * 60_000,
    enabled: useSignedIn(),
  })
}

/** Every connector with the user's connection, and whether this server sends at all. */
export function useConnectors(refetchInterval: number | false = false) {
  return useQuery({
    queryKey: queryKeys.connectors,
    queryFn: async () => (await callTool('connectors.list', {})).result,
    refetchInterval,
    enabled: useSignedIn(),
  })
}

export function useOutbox(list: OutboxList) {
  return useQuery({
    queryKey: queryKeys.outboxList(list),
    queryFn: async () => (await callTool('outbox.list', { status: list })).result,
    enabled: useSignedIn(),
  })
}

/**
 * The user's time off: periods that ended in the last 90 days, current and upcoming ones, by
 * first day. Refreshed live when time off or the working time changes (lib/session.tsx).
 */
/** What `time_off.list` answers: the periods, and out-of-office time suggested as time off. */
export type TimeOffList = ToolOutput<'time_off.list'>

function useTimeOffList<T>(select: (list: TimeOffList) => T) {
  return useQuery({
    queryKey: queryKeys.timeOff,
    queryFn: async () => (await callTool('time_off.list', {})).result,
    staleTime: 5 * 60_000,
    enabled: useSignedIn(),
    select,
  })
}

const periodsOf = (list: TimeOffList) => list.periods
const suggestionsOf = (list: TimeOffList) => list.suggestions
const awayOf = (list: TimeOffList) => list.away

/** The user's time off (ended in the last 90 days, current and future). */
export function useTimeOff() {
  return useTimeOffList(periodsOf)
}

/** Away now or soon, as the API says (all time off, absences merged); null: not away. */
export function useAway() {
  return useTimeOffList(awayOf)
}

/** The tokens that let Claude Code (MCP) act as the user (Settings → Claude Code). */
export function useAccessTokens() {
  return useQuery({
    queryKey: queryKeys.accessTokens,
    queryFn: async () => (await callTool('access_tokens.list', {})).result.tokens,
    enabled: useSignedIn(),
  })
}

/** Out-of-office time from connected calendars that hugo suggests adding as time off. */
export function useTimeOffSuggestions() {
  return useTimeOffList(suggestionsOf)
}

/** The user's day from now (`today.brief`): time for tasks after meetings, hugo's plan, … */
export function useToday() {
  return useQuery({
    queryKey: queryKeys.today,
    queryFn: async () => (await callTool('today.brief', {})).result,
    enabled: useSignedIn(),
  })
}

/**
 * Who should hear from the user now: late promises, then people they wait on before time off.
 * Follows every change to items, time off, settings and the Outbox.
 */
export function useHeadsUps() {
  return useQuery({
    queryKey: queryKeys.headsUps,
    queryFn: async () => (await callTool('headsups.list', {})).result.headsUps,
    enabled: useSignedIn(),
  })
}

/** The tags of active items with their counts, and which are personal. */
export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags,
    queryFn: async () => (await callTool('tags.list', {})).result.tags,
    enabled: useSignedIn(),
  })
}

/** The board: open work in columns with each column's time, from hugo's planner. */
export function useBoard() {
  return useQuery({
    queryKey: queryKeys.board,
    queryFn: async () => (await callTool('board.view', {})).result,
    enabled: useSignedIn(),
  })
}

/**
 * Whether hugo's AI is on, whether it files clear asks by itself, and today's AI cost. Refreshed
 * live on suggestions and settings changes (lib/session.tsx) and after a draft.
 */
export function useAiStatus() {
  return useQuery({
    queryKey: queryKeys.ai,
    queryFn: async () => (await callTool('ai.status', {})).result,
    staleTime: 60_000,
    enabled: useSignedIn(),
  })
}

/** The most recent undoable change, for the `z` shortcut and the palette. */
type LastChange = { actionId: string; label: string } | null
let lastChange: LastChange = null
/**
 * An undoable change that is still on its way to the server. The UI updates optimistically, so
 * `z` can arrive before the server confirmed; undo then waits for it instead of doing nothing.
 */
let inflight: Promise<LastChange> | null = null
const listeners = new Set<() => void>()
function setLastChange(value: LastChange) {
  lastChange = value
  for (const listener of listeners) listener()
}
/** Whether `z` has something to undo (a confirmed or an in-flight change). */
export function hasUndoable(): boolean {
  return inflight !== null || lastChange !== null
}

export function useLastChange(): LastChange {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => lastChange,
  )
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Something went wrong. Please try again.'
}

export type RunOptions<N extends ToolName> = {
  /** Toast text on success; with Undo when the change can be undone. */
  done?: string | ((result: ToolOutput<N>) => string)
  /** A second line on the success toast, e.g. what a move changed for other items. */
  detail?: (result: ToolOutput<N>) => string | null
  /** A button on the success toast of a change that cannot be undone, e.g. "Open Outbox". */
  doneAction?: { label: string; onClick: () => void }
  /** Toast title when the call fails (default "Could not save"). */
  failed?: string
  /** Handle a failure yourself: return true and no error toast is shown. */
  onError?: (error: unknown) => boolean
  /**
   * Remove the item from a list right away, or with `patch` change it there (e.g. drop its
   * suggestion); rolled back if the call fails.
   */
  optimistic?: { view: ItemView; itemId: string; patch?: Partial<Item> }
  via?: 'ui' | 'palette' | 'voice'
}

/** Run a tool that changes something, with optimistic UI, an undo toast and cache refresh. */
export function useRunTool() {
  const queryClient = useQueryClient()

  /** Refetch what a change may move, once this tab's changes in flight have all finished. */
  const refresh = useCallback(
    (tool?: ToolName) => {
      const roots = [
        'items',
        'insights',
        // Only refetched where shown (Outbox, Settings); a reply or an approval changes them.
        'outbox',
        'connectors',
        // Heads-ups follow from items, time off, settings and the Outbox: any change may move them.
        'headsups',
      ]
      // Working days of a period depend on the working time; the header shows time off always.
      if (tool?.startsWith('time_off.') || tool === 'settings.update') roots.push('time-off')
      refresher.refresh(queryClient, roots)
    },
    [queryClient],
  )

  const undo = useCallback(
    async (actionId: string) => {
      const done = refresher.begin()
      try {
        const { result } = await callTool('action.undo', { actionId })
        if (lastChange?.actionId === actionId) setLastChange(null)
        toast.add({
          title: 'Undone',
          description: undoLabel(result.tool),
          type: 'success',
          timeout: 2500,
        })
      } catch (error) {
        log.warn('undo failed', errorMessage(error))
        toast.add({ title: 'Could not undo', description: errorMessage(error), type: 'error' })
      } finally {
        refresh()
        done()
      }
    },
    [refresh],
  )

  const run = useCallback(
    async <N extends ToolName>(
      name: N,
      input: ToolInput<N>,
      options: RunOptions<N> = {},
    ): Promise<ToolOutput<N> | null> => {
      const done = refresher.begin()
      const key = options.optimistic ? queryKeys.view(options.optimistic.view) : null
      let snapshot: Item[] | undefined
      let settle: (value: LastChange) => void = () => {}
      if (toolSpecs[name].undoable) {
        const pending = new Promise<LastChange>((resolve) => {
          settle = resolve
        })
        inflight = pending
        void pending.then(() => {
          if (inflight === pending) inflight = null
        })
      }
      if (key && options.optimistic) {
        await queryClient.cancelQueries({ queryKey: key })
        snapshot = queryClient.getQueryData<Item[]>(key)
        const { itemId, patch } = options.optimistic
        queryClient.setQueryData<Item[]>(key, (old) =>
          patch
            ? old?.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
            : old?.filter((item) => item.id !== itemId),
        )
      }
      try {
        const { result, action } = await callTool(
          name,
          input,
          options.via ? { via: options.via } : {},
        )
        const label = typeof options.done === 'function' ? options.done(result) : options.done
        const description = options.detail?.(result) ?? undefined
        if (action?.undoable) {
          const actionId = action.id
          setLastChange({ actionId, label: label ?? 'Last change' })
          settle({ actionId, label: label ?? 'Last change' })
          if (label) {
            toast.add({
              title: label,
              description,
              // Longer when it says what else changed: there is more to read.
              timeout: description ? 9000 : 6000,
              actionProps: { children: 'Undo', onClick: () => void undo(actionId) },
            })
          }
        } else if (label && options.doneAction) {
          const { label: children, onClick } = options.doneAction
          const toastId = toast.add({
            title: label,
            type: 'success',
            timeout: 6000,
            actionProps: {
              children,
              onClick: () => {
                toast.close(toastId)
                onClick()
              },
            },
          })
        } else if (label) {
          toast.add({ title: label, type: 'success', timeout: 3000 })
        }
        return result
      } catch (error) {
        settle(null)
        if (key && snapshot) queryClient.setQueryData(key, snapshot)
        log.warn(`${name} failed`, errorMessage(error))
        if (!options.onError?.(error)) {
          toast.add({
            title: options.failed ?? 'Could not save',
            description: errorMessage(error),
            type: 'error',
          })
        }
        return null
      } finally {
        settle(lastChange)
        refresh(name)
        done()
      }
    },
    [queryClient, refresh, undo],
  )

  /** Undo the most recent change, waiting for one that is still being saved. */
  const undoLast = useCallback(async () => {
    const target = inflight ? await inflight : lastChange
    if (target) await undo(target.actionId)
  }, [undo])

  return { run, undo, undoLast }
}

const UNDO_LABELS: Record<string, string> = {
  'item.capture': 'Capture removed',
  'item.create': 'Item removed',
  'item.triage': 'Back in the inbox',
  'item.complete': 'Marked open again',
  'item.drop': 'Restored',
  'item.snooze': 'Snooze cancelled',
  'item.set_kind': 'Moved back',
  'item.move': 'Moved back',
}

function undoLabel(tool: string): string {
  return UNDO_LABELS[tool] ?? 'Change reverted'
}
